import React, { cloneElement, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { IconButton } from '@fabric/button';
import { clsx } from '~styles';
import { Resizer, changeIndex, useResizer } from './utils';
import { useStyles } from './carousel.styles';
import { CarouselProps, CustomButton } from './types';
import { CarouselBase } from './carousel-base';

const defaultNextButton = () => (
	<IconButton
		aria-label={window.jQuery ? $.__('Next') : 'Next'}
		color="secondary"
		icon="chevron-right-solid"
		type="button"
		variant="outlined"
	/>
);

const defaultPrevButton = () => (
	<IconButton
		aria-label={window.jQuery ? $.__('Previous') : 'Previous'}
		color="secondary"
		icon="chevron-left-solid"
		type="button"
		variant="outlined"
	/>
);

export function Carousel({
	ariaLabelledBy,
	containerRole,
	nextButton = defaultNextButton(),
	prevButton = defaultPrevButton(),
	singlePageSlideAlignment,
	slideGap,
	slides,
	widthBreakpoints = { 0: 1 },
}: CarouselProps): ReactElement {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [slidesToShow, setSlidesToShow] = useState(1);
	const carouselBaseRef = useRef<HTMLDivElement>(null);
	const prevButtonRef = useRef<HTMLDivElement>(null);
	const [prevButtonWidth, setPrevButtonWidth] = useState(0);
	const nextButtonRef = useRef<HTMLDivElement>(null);
	const [nextButtonWidth, setNextButtonWidth] = useState(0);
	const isFirstPage = currentIndex <= 0;
	const isLastPage = currentIndex >= slides.length - slidesToShow;
	const isSinglePage = slides.length <= slidesToShow;
	const { classes } = useStyles({ isSinglePage });

	const getButtonWidth = useMemo((): number => {
		let carouselButtonWidth = 0;
		if (isSinglePage) {
			carouselButtonWidth = nextButtonWidth + prevButtonWidth;
		} else if (isFirstPage) {
			carouselButtonWidth = nextButtonWidth;
		} else if (isLastPage) {
			carouselButtonWidth = prevButtonWidth;
		}

		return carouselButtonWidth;
	}, [isFirstPage, isLastPage, isSinglePage, nextButtonWidth, prevButtonWidth]);

	useResizer({
		elementRef: carouselBaseRef,
		breakpoints: widthBreakpoints,
		onResize: (numToShow: number) => {
			changeIndex({
				difference: 0,
				numSlides: slides.length,
				slidesToShow: numToShow,
				currentIndex,
				setCurrentIndex,
			});
			setSlidesToShow(numToShow);
		},
		carouselButtonWidth: getButtonWidth,
	});

	useEffect(() => {
		if (nextButtonRef?.current) {
			setNextButtonWidth(nextButtonRef.current.getBoundingClientRect().width);
		}

		if (prevButtonRef?.current) {
			setPrevButtonWidth(prevButtonRef.current.getBoundingClientRect().width);
		}
	});

	return (
		<div className={classes.wrapper}>
			{!isSinglePage && (
				<div className={clsx(classes.prevButton, classes.button, { [classes.hideButton]: isFirstPage })} ref={prevButtonRef}>
					{cloneElement(prevButton, {
						onClick: evt => {
							changeIndex({
								difference: -slidesToShow,
								numSlides: slides.length,
								currentIndex,
								setCurrentIndex,
							});
							// preserve original onClick for custom buttons
							return (prevButton as CustomButton)?.props?.onClick?.(evt);
						},
						disabled: isFirstPage,
						tabIndex: isFirstPage ? -1 : 0,
					})}
				</div>
			)}
			{!isSinglePage && (
				<div className={clsx(classes.nextButton, classes.button, { [classes.hideButton]: isLastPage })} ref={nextButtonRef}>
					{cloneElement(nextButton, {
						onClick: evt => {
							changeIndex({
								difference: slidesToShow,
								numSlides: slides.length,
								currentIndex,
								setCurrentIndex,
							});
							// preserve original onClick for custom buttons
							return (nextButton as CustomButton)?.props?.onClick?.(evt);
						},
						disabled: isLastPage,
						tabIndex: isLastPage ? -1 : 0,
					})}
				</div>
			)}

			<div className={classes.baseContainer}>
				<CarouselBase
					ariaLabelledBy={ariaLabelledBy}
					containerRef={carouselBaseRef}
					containerRole={containerRole}
					currentIndex={currentIndex}
					singlePageSlideAlignment={isSinglePage ? singlePageSlideAlignment : 'center'}
					slideGap={slideGap}
					slides={slides}
					slidesToShow={slidesToShow}
				/>
			</div>
		</div>
	);
}

Carousel.Base = CarouselBase;
Carousel.Resizer = Resizer;
Carousel.changeIndex = changeIndex;
Carousel.useResizer = useResizer;
